import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/hooks/useOrganization';
import { 
  UserPlus, Mail, Users, Crown, Copy, CheckCircle,
  AlertTriangle, Trash2, User
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  userPlan: 'basic' | 'individual' | 'corporate' | 'enterprise';
}

interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  organization_id: string;
  user_id?: string;
  profile_picture?: string;
}

export const TeamManagementModal = ({ isOpen, onClose, userPlan }: TeamManagementModalProps) => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('sustainability_officer');
  const { user } = useAuth();
  const { toast } = useToast();
  const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

  const getMaxTeamSize = (plan: string = 'basic') => {
    switch (plan) {
      case 'basic': return 0;
      case 'individual': return 2;
      case 'corporate': return 5;
      case 'enterprise': return 20;
      default: return 1;
    }
  };

  const maxTeamSize = getMaxTeamSize(userPlan);
  const currentTeamCount = teamMembers.filter(m => m.status === 'active' || m.status === 'pending').length;

  // ─── SEND EMAIL HELPER ──────────────────────────────────────────────────────
  const sendEmail = async (to: string, subject: string, html: string) => {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { to, subject, html },
    });
    if (error) throw new Error(`Failed to send email: ${error.message}`);
    if (data && !data.success) throw new Error(data.error || "Failed to send email");
    return data;
  };

  // ─── EMAIL TEMPLATES ────────────────────────────────────────────────────────
  const getInviteNewUserEmail = (name: string, role: string, signupLink: string) => `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
        <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
      </div>
      <h2 style="color: #1a1a1a;">You've been invited to join GreenCard! 🌱</h2>
      <p style="color: #444; line-height: 1.6;">
        Hi <strong>${name}</strong>, you've been invited to join the team as a
        <strong>${formatRole(role)}</strong>.
      </p>
      <p style="color: #444; line-height: 1.6;">Click the button below to create your account:</p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${signupLink}"
          style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                 text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
          Accept Invitation & Sign Up
        </a>
      </div>
      <p style="color: #666; font-size: 14px; line-height: 1.6;">
        This invitation link will expire in <strong>7 days</strong>.
        If you weren't expecting this, you can safely ignore this email.
      </p>
      <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
      <p style="color: #999; font-size: 12px; text-align: center;">
        GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
        <a href="${signupLink}" style="color: #16a34a;">${signupLink}</a>
      </p>
    </div>
  `;

  const getInviteExistingUserEmail = (name: string, role: string, dashboardLink: string) => `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
        <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
      </div>
      <h2 style="color: #1a1a1a;">You've been added to a team! 🌱</h2>
      <p style="color: #444; line-height: 1.6;">
        Hi <strong>${name}</strong>, you've been added to a GreenCard organization as a
        <strong>${formatRole(role)}</strong>.
      </p>
      <div style="text-align: center; margin: 30px 0;">
        <a href="${dashboardLink}"
          style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                 text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
          Go to Dashboard
        </a>
      </div>
      <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
      <p style="color: #999; font-size: 12px; text-align: center;">GreenCard Sustainability Platform</p>
    </div>
  `;

  // ─── FETCH TEAM MEMBERS ─────────────────────────────────────────────────────
  const fetchTeamMembers = async () => {
    if (!organizationId || orgLoading) return;
    try {
      const { data: members, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) {
        toast({ title: "Error", description: "Failed to load team members", variant: "destructive" });
        return;
      }
      setTeamMembers(members || []);
    } catch (error) {
      console.error('Error in fetchTeamMembers:', error);
    }
  };

  useEffect(() => {
    if (isOpen && organizationId && !orgLoading) {
      fetchTeamMembers();
    }
  }, [isOpen, organizationId, orgLoading]);

  // ─── ADD TEAM MEMBER ────────────────────────────────────────────────────────
  const addTeamMember = async () => {
    if (!inviteEmail || !inviteName || !organizationId) return;

    if (currentTeamCount >= maxTeamSize) {
      toast({
        title: 'Team Limit Reached',
        description: `Maximum team size for your plan is ${maxTeamSize}.`,
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const existingMember = teamMembers.find(m =>
        m.email.toLowerCase() === inviteEmail.toLowerCase()
      );
      if (existingMember) {
        toast({ title: "Member Already Exists", description: `${inviteEmail} is already in your team.`, variant: "destructive" });
        return;
      }

      const { data: existingProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, user_id')
        .eq('user_id', (
          await supabase.from('user_profiles').select('user_id').limit(1) // just to get the join right
        ).data?.[0]?.user_id || '')
        .limit(0); // we don't actually use this — see below

      // Check by auth email instead (user_profiles has no email column)
      // We'll treat all invites as 'pending' and let the verify-email edge function
      // handle activation. This is the safest approach.
      const userExists = false; // Always pending for new invites - edge fn activates on verify

      const { error } = await supabase
        .from('team_members')
        .insert({
          organization_id: organizationId,
          user_id: null,
          full_name: inviteName,
          email: inviteEmail.toLowerCase(),
          role: inviteRole,
          status: 'pending',
          joined_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast({ title: "Already in Team", description: "This email is already associated with your team.", variant: "destructive" });
          return;
        }
        throw error;
      }

      // Build invite link with name AND role encoded
      // ── KEY: name and role travel in the URL so signup page can pre-fill them ──
      const signupLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(inviteEmail)}&org=${organizationId}&name=${encodeURIComponent(inviteName)}&role=${encodeURIComponent(inviteRole)}`;

      try {
        await sendEmail(
          inviteEmail,
          "You've been invited to join GreenCard",
          getInviteNewUserEmail(inviteName, inviteRole, signupLink)
        );
        toast({
          title: "Invitation Sent",
          description: `Invitation email sent to ${inviteEmail}.`,
        });
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        toast({
          title: "Member Added",
          description: `${inviteName} was added but the email failed. Copy their invite link manually.`,
          variant: "destructive",
        });
      }

      setInviteEmail('');
      setInviteName('');
      fetchTeamMembers();

    } catch (error: any) {
      console.error('Error adding team member:', error);
      toast({ title: "Error", description: error.message || "Failed to add team member", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // ─── RESEND INVITATION ──────────────────────────────────────────────────────
  const resendInvitation = async (member: TeamMember) => {
    try {
      const signupLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(member.email)}&org=${organizationId}&name=${encodeURIComponent(member.full_name)}&role=${encodeURIComponent(member.role)}`;
      await sendEmail(
        member.email,
        "Reminder: You've been invited to join GreenCard",
        getInviteNewUserEmail(member.full_name, member.role, signupLink)
      );
      toast({ title: "Invitation Resent", description: `Invitation resent to ${member.email}.` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to resend invitation.", variant: "destructive" });
    }
  };

  // ─── REMOVE MEMBER ──────────────────────────────────────────────────────────
  const removeTeamMember = async (memberId: string, memberName: string) => {
    if (!organizationId || !user) return;
    try {
      const memberToRemove = teamMembers.find(m => m.id === memberId);
      if (memberToRemove?.email === user.email) {
        toast({ title: "Cannot Remove Yourself", description: "You cannot remove yourself as the organization admin.", variant: "destructive" });
        return;
      }
      const { error } = await supabase.from('team_members').delete().eq('id', memberId).eq('organization_id', organizationId);
      if (error) throw error;
      toast({ title: "Member Removed", description: `${memberName} has been removed.` });
      fetchTeamMembers();
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove team member", variant: "destructive" });
    }
  };

  // ─── UPDATE ROLE ────────────────────────────────────────────────────────────
  const updateMemberRole = async (memberId: string, newRole: string) => {
    if (!organizationId) return;
    try {
      const member = teamMembers.find(m => m.id === memberId);
      if (member?.role === 'admin') {
        toast({ title: "Cannot Change Admin Role", variant: "destructive" });
        return;
      }
      const { error } = await supabase.from('team_members').update({ role: newRole, updated_at: new Date().toISOString() }).eq('id', memberId).eq('organization_id', organizationId);
      if (error) throw error;
      toast({ title: "Role Updated" });
      fetchTeamMembers();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update role", variant: "destructive" });
    }
  };

  // ─── COPY INVITE LINK ───────────────────────────────────────────────────────
  const copyInviteLink = (member: TeamMember) => {
    const inviteLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(member.email)}&org=${organizationId}&name=${encodeURIComponent(member.full_name)}&role=${encodeURIComponent(member.role)}`;
    navigator.clipboard.writeText(inviteLink);
    toast({ title: "Invite Link Copied" });
  };

  // ─── HELPERS ────────────────────────────────────────────────────────────────
  const getStatusIcon = (status: string) => status === 'active'
    ? <CheckCircle className="h-4 w-4 text-green-500" />
    : <AlertTriangle className="h-4 w-4 text-orange-500" />;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role: string) =>
    role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // ─── INDIVIDUAL ACCOUNT GUARD ────────────────────────────────────────────────
  if (!isOrganization && !orgLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Team Management</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Individual Account</h3>
            <p className="text-gray-600 mb-4">Team management is only available for organizations.</p>
            <Button onClick={onClose} className="w-full">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Team Management</DialogTitle>
          <DialogDescription>Manage your sustainability team members and collaboration access</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Plan Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-600" />
                Current Plan: {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{teamMembers.filter(m => m.role === 'admin').length}</div>
                  <div className="text-sm text-gray-500">Admins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{teamMembers.filter(m => m.role !== 'admin').length}</div>
                  <div className="text-sm text-gray-500">Team Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{maxTeamSize}</div>
                  <div className="text-sm text-gray-500">Max Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{teamMembers.filter(m => m.status === 'pending').length}</div>
                  <div className="text-sm text-gray-500">Pending</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add New Member */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" />Add Team Member</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" placeholder="John Doe" value={inviteName} onChange={(e) => setInviteName(e.target.value)} disabled={currentTeamCount >= maxTeamSize} />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" placeholder="john@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} disabled={currentTeamCount >= maxTeamSize} />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole} disabled={currentTeamCount >= maxTeamSize}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
                      <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
                      <SelectItem value="analyst">ESG Analyst</SelectItem>
                      <SelectItem value="manager">ESG Manager</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={addTeamMember} disabled={loading || !inviteEmail || !inviteName || currentTeamCount >= maxTeamSize}>
                  <Mail className="h-4 w-4 mr-2" />
                  {loading ? 'Sending Invite...' : 'Add & Send Invite'}
                </Button>
              </div>
              {currentTeamCount >= maxTeamSize && (
                <Alert><AlertTriangle className="h-4 w-4" /><AlertDescription>Team limit reached. Upgrade to add more members.</AlertDescription></Alert>
              )}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Invited members will receive an email with a pre-filled signup link. Their role will be automatically assigned when they verify their email.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Team Members List */}
          <Card>
            <CardHeader><CardTitle>Team Members ({teamMembers.length})</CardTitle></CardHeader>
            <CardContent>
              {teamMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No team members yet. Add your first team member above.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {member.profile_picture ? (
                              <img src={member.profile_picture} alt={member.full_name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xs font-medium">{member.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                              </div>
                            )}
                            <div>
                              <div>{member.full_name}</div>
                              {member.role === 'admin' && <Badge variant="outline" className="text-xs">Admin</Badge>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Select value={member.role} onValueChange={(newRole) => updateMemberRole(member.id, newRole)} disabled={member.role === 'admin'}>
                            <SelectTrigger className="w-40"><SelectValue>{formatRole(member.role)}</SelectValue></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
                              <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
                              <SelectItem value="analyst">ESG Analyst</SelectItem>
                              <SelectItem value="manager">ESG Manager</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(member.status)}>
                            <div className="flex items-center gap-1">{getStatusIcon(member.status)}{member.status}</div>
                          </Badge>
                        </TableCell>
                        <TableCell>{member.joined_at ? new Date(member.joined_at).toLocaleDateString() : 'Not yet joined'}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {member.status === 'pending' && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => resendInvitation(member)} title="Resend invitation email"><Mail className="h-4 w-4" /></Button>
                                <Button variant="outline" size="sm" onClick={() => copyInviteLink(member)} title="Copy invite link"><Copy className="h-4 w-4" /></Button>
                              </>
                            )}
                            {member.role !== 'admin' && (
                              <Button variant="outline" size="sm" onClick={() => removeTeamMember(member.id, member.full_name)} className="text-red-600 hover:text-red-700" title="Remove member"><Trash2 className="h-4 w-4" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end"><Button onClick={onClose}>Close</Button></div>
      </DialogContent>
    </Dialog>
  );
};








// import React, { useState, useEffect } from 'react';
// import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Badge } from '@/components/ui/badge';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { useToast } from '@/hooks/use-toast';
// import { useOrganization } from '@/hooks/useOrganization';
// import { 
//   UserPlus, 
//   Mail, 
//   Users, 
//   Crown, 
//   Copy, 
//   CheckCircle,
//   AlertTriangle,
//   Trash2,
//   User
// } from 'lucide-react';
// import { supabase } from '@/integrations/supabase/client';

// interface TeamManagementModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   userPlan: 'individual' | 'corporate' | 'enterprise';
// }

// interface TeamMember {
//   id: string;
//   full_name: string;
//   email: string;
//   role: string;
//   status: string;
//   created_at: string;
//   organization_id: string;
//   user_id?: string;
//   profile_picture?: string;
// }

// export const TeamManagementModal = ({ isOpen, onClose, userPlan }: TeamManagementModalProps) => {
//   const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [inviteEmail, setInviteEmail] = useState('');
//   const [inviteName, setInviteName] = useState('');
//   const [inviteRole, setInviteRole] = useState('sustainability_officer');
//   const { user } = useAuth();
//   const { toast } = useToast();
  
//   const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

//   const getMaxTeamSize = (plan: string = 'individual') => {
//     switch (plan) {
//       case 'individual': return 2;
//       case 'corporate': return 5;
//       case 'enterprise': return 20;
//       default: return 1;
//     }
//   };

//   const maxTeamSize = getMaxTeamSize(userPlan);
//   const currentTeamCount = teamMembers.filter(m => m.status === 'active' || m.status === 'pending').length;

//   // ─── SEND EMAIL HELPER ──────────────────────────────────────────────────────
//   const sendEmail = async (to: string, subject: string, html: string) => {
//     const { data, error } = await supabase.functions.invoke("send-email", {
//       body: { to, subject, html },
//     });

//     if (error) throw new Error(`Failed to send email: ${error.message}`);
//     if (data && !data.success) throw new Error(data.error || "Failed to send email");

//     return data;
//   };

//   // ─── EMAIL TEMPLATES ────────────────────────────────────────────────────────
//   const getInviteNewUserEmail = (name: string, role: string, signupLink: string) => `
//     <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
//       <div style="text-align: center; margin-bottom: 30px;">
//         <h1 style="color: #16a34a; margin: 0;">GreenCard</h1>
//         <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
//       </div>

//       <h2 style="color: #1a1a1a;">You've been invited to join GreenCard! 🌱</h2>
//       <p style="color: #444; line-height: 1.6;">
//         Hi <strong>${name}</strong>, you've been invited to join the team as a 
//         <strong>${formatRole(role)}</strong>.
//       </p>
//       <p style="color: #444; line-height: 1.6;">
//         Click the button below to create your account and get started:
//       </p>

//       <div style="text-align: center; margin: 30px 0;">
//         <a href="${signupLink}"
//           style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
//                  text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
//           Accept Invitation & Sign Up
//         </a>
//       </div>

//       <p style="color: #666; font-size: 14px; line-height: 1.6;">
//         This invitation link will expire in <strong>7 days</strong>. 
//         If you weren't expecting this invitation, you can safely ignore this email.
//       </p>

//       <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
//       <p style="color: #999; font-size: 12px; text-align: center;">
//         GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
//         <a href="${signupLink}" style="color: #16a34a;">${signupLink}</a>
//       </p>
//     </div>
//   `;

//   const getInviteExistingUserEmail = (name: string, role: string, dashboardLink: string) => `
//     <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
//       <div style="text-align: center; margin-bottom: 30px;">
//         <h1 style="color: #16a34a; margin: 0;">GreenCard</h1>
//         <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
//       </div>

//       <h2 style="color: #1a1a1a;">You've been added to a team! 🌱</h2>
//       <p style="color: #444; line-height: 1.6;">
//         Hi <strong>${name}</strong>, you've been added to a GreenCard organization as a 
//         <strong>${formatRole(role)}</strong>.
//       </p>
//       <p style="color: #444; line-height: 1.6;">
//         Log in to your account to access the shared workspace:
//       </p>

//       <div style="text-align: center; margin: 30px 0;">
//         <a href="${dashboardLink}"
//           style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
//                  text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
//           Go to Dashboard
//         </a>
//       </div>

//       <p style="color: #666; font-size: 14px; line-height: 1.6;">
//         If you weren't expecting this, please contact your organization admin.
//       </p>

//       <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
//       <p style="color: #999; font-size: 12px; text-align: center;">
//         GreenCard Sustainability Platform
//       </p>
//     </div>
//   `;

//   // ─── FETCH TEAM MEMBERS ─────────────────────────────────────────────────────
//   const fetchTeamMembers = async () => {
//     if (!organizationId || orgLoading) return;
    
//     try {
//       const { data: members, error } = await supabase
//         .from('team_members')
//         .select('*')
//         .eq('organization_id', organizationId)
//         .order('created_at', { ascending: false });

//       if (error) {
//         console.error('Error fetching team members:', error);
//         toast({
//           title: "Error",
//           description: "Failed to load team members",
//           variant: "destructive"
//         });
//         return;
//       }

//       setTeamMembers(members || []);
//     } catch (error) {
//       console.error('Error in fetchTeamMembers:', error);
//     }
//   };

//   useEffect(() => {
//     if (isOpen && organizationId && !orgLoading) {
//       fetchTeamMembers();
//     }
//   }, [isOpen, organizationId, orgLoading]);

//   // ─── ADD TEAM MEMBER ────────────────────────────────────────────────────────
//   const addTeamMember = async () => {
//     if (!inviteEmail || !inviteName || !organizationId) return;
    
//     if (currentTeamCount >= maxTeamSize) {
//       toast({
//         title: 'Team Limit Reached',
//         description: `You have reached the maximum number of team members for your plan (${maxTeamSize}).`,
//         variant: 'destructive',
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       // Check if email already exists in team
//       const existingMember = teamMembers.find(member => 
//         member.email.toLowerCase() === inviteEmail.toLowerCase()
//       );
      
//       if (existingMember) {
//         toast({
//           title: "Member Already Exists",
//           description: `${inviteEmail} is already in your team.`,
//           variant: "destructive"
//         });
//         return;
//       }

//       // Check if user already has a GreenCard account
//       const { data: existingProfile, error: profileError } = await supabase
//         .from('profiles')
//         .select('id')
//         .eq('email', inviteEmail.toLowerCase())
//         .maybeSingle();

//       if (profileError && profileError.code !== 'PGRST116') {
//         console.error('Error checking for existing user:', profileError);
//       }

//       const userExists = !!existingProfile;

//       // Add to team_members table
//       const { data: newMember, error } = await supabase
//         .from('team_members')
//         .insert({
//           organization_id: organizationId,
//           user_id: existingProfile?.id || null,
//           full_name: inviteName,
//           email: inviteEmail.toLowerCase(),
//           role: inviteRole,
//           status: userExists ? 'active' : 'pending',
//           joined_at: userExists ? new Date().toISOString() : null,
//           created_at: new Date().toISOString(),
//           updated_at: new Date().toISOString()
//         })
//         .select()
//         .single();

//       if (error) {
//         if (error.code === '23505') {
//           toast({
//             title: "Already in Team",
//             description: "This email is already associated with your team.",
//             variant: "destructive"
//           });
//           return;
//         }
//         throw error;
//       }

//       // Send invitation email
//       try {
//         if (userExists) {
//           // Existing user: notify them they've been added
//           await sendEmail(
//             inviteEmail,
//             "You've been added to a GreenCard team",
//             getInviteExistingUserEmail(
//               inviteName,
//               inviteRole,
//               `${window.location.origin}/`
//             )
//           );
//         } else {
//           // New user: send signup invitation link
//           const signupLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(inviteEmail)}&org=${organizationId}`;
//           await sendEmail(
//             inviteEmail,
//             "You've been invited to join GreenCard",
//             getInviteNewUserEmail(inviteName, inviteRole, signupLink)
//           );
//         }

//         toast({
//           title: userExists ? "Team Member Added" : "Invitation Sent",
//           description: userExists
//             ? `${inviteName} has been added and notified by email.`
//             : `Invitation email sent to ${inviteEmail}.`,
//         });
//       } catch (emailError) {
//         // Member was added to DB but email failed - warn but don't block
//         console.error('Email sending failed:', emailError);
//         toast({
//           title: userExists ? "Team Member Added" : "Member Added",
//           description: `${inviteName} was added to the team, but the notification email failed to send. You can copy their invite link manually.`,
//           variant: "destructive",
//         });
//       }

//       setInviteEmail('');
//       setInviteName('');
//       fetchTeamMembers();

//     } catch (error: any) {
//       console.error('Error adding team member:', error);
//       toast({
//         title: "Error",
//         description: error.message || "Failed to add team member",
//         variant: "destructive"
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── RESEND INVITATION EMAIL ────────────────────────────────────────────────
//   const resendInvitation = async (member: TeamMember) => {
//     try {
//       const signupLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(member.email)}&org=${organizationId}`;
      
//       await sendEmail(
//         member.email,
//         "Reminder: You've been invited to join GreenCard",
//         getInviteNewUserEmail(member.full_name, member.role, signupLink)
//       );

//       toast({
//         title: "Invitation Resent",
//         description: `Invitation email resent to ${member.email}.`,
//       });
//     } catch (error) {
//       console.error('Error resending invitation:', error);
//       toast({
//         title: "Error",
//         description: "Failed to resend invitation email.",
//         variant: "destructive"
//       });
//     }
//   };

//   // ─── REMOVE TEAM MEMBER ─────────────────────────────────────────────────────
//   const removeTeamMember = async (memberId: string, memberName: string) => {
//     if (!organizationId || !user) return;

//     try {
//       const memberToRemove = teamMembers.find(m => m.id === memberId);
//       if (memberToRemove?.email === user.email) {
//         toast({
//           title: "Cannot Remove Yourself",
//           description: "You cannot remove yourself as the organization admin.",
//           variant: "destructive"
//         });
//         return;
//       }

//       const { error } = await supabase
//         .from('team_members')
//         .delete()
//         .eq('id', memberId)
//         .eq('organization_id', organizationId);

//       if (error) throw error;

//       toast({
//         title: "Member Removed",
//         description: `${memberName} has been removed from your team.`,
//       });

//       fetchTeamMembers();
//     } catch (error) {
//       console.error('Error removing team member:', error);
//       toast({
//         title: "Error",
//         description: "Failed to remove team member",
//         variant: "destructive"
//       });
//     }
//   };

//   // ─── UPDATE MEMBER ROLE ─────────────────────────────────────────────────────
//   const updateMemberRole = async (memberId: string, newRole: string) => {
//     if (!organizationId) return;

//     try {
//       const member = teamMembers.find(m => m.id === memberId);
//       if (member?.role === 'admin') {
//         toast({
//           title: "Cannot Change Admin Role",
//           description: "Admin role cannot be changed.",
//           variant: "destructive"
//         });
//         return;
//       }

//       const { error } = await supabase
//         .from('team_members')
//         .update({ 
//           role: newRole,
//           updated_at: new Date().toISOString()
//         })
//         .eq('id', memberId)
//         .eq('organization_id', organizationId);

//       if (error) throw error;

//       toast({
//         title: "Role Updated",
//         description: "Team member role has been updated.",
//       });

//       fetchTeamMembers();
//     } catch (error) {
//       console.error('Error updating member role:', error);
//       toast({
//         title: "Error",
//         description: "Failed to update member role",
//         variant: "destructive"
//       });
//     }
//   };

//   // ─── HELPERS ────────────────────────────────────────────────────────────────
//   const getStatusIcon = (status: string) => {
//     switch (status) {
//       case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
//       default: return <AlertTriangle className="h-4 w-4 text-orange-500" />;
//     }
//   };

//   const getStatusColor = (status: string) => {
//     switch (status) {
//       case 'active': return 'bg-green-100 text-green-800';
//       case 'pending': return 'bg-orange-100 text-orange-800';
//       default: return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const formatRole = (role: string) => {
//     return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
//   };

//   const copyInviteLink = (member: TeamMember) => {
//     const inviteLink = `${window.location.origin}/auth?invite=true&email=${encodeURIComponent(member.email)}&org=${organizationId}`;
//     navigator.clipboard.writeText(inviteLink);
//     toast({
//       title: "Invite Link Copied",
//       description: "Share this link with the team member to join.",
//     });
//   };

//   // ─── INDIVIDUAL ACCOUNT GUARD ────────────────────────────────────────────────
//   if (!isOrganization && !orgLoading) {
//     return (
//       <Dialog open={isOpen} onOpenChange={onClose}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2">
//               <Users className="h-5 w-5" />
//               Team Management
//             </DialogTitle>
//           </DialogHeader>
//           <div className="py-6 text-center">
//             <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
//             <h3 className="text-lg font-semibold mb-2">Individual Account</h3>
//             <p className="text-gray-600 mb-4">
//               Team management is only available for organizations.
//             </p>
//             <Button onClick={onClose} className="w-full">Close</Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//     );
//   }

//   // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
//   return (
//     <Dialog open={isOpen} onOpenChange={onClose}>
//       <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <Users className="h-5 w-5" />
//             Team Management
//           </DialogTitle>
//           <DialogDescription>
//             Manage your sustainability team members and collaboration access
//           </DialogDescription>
//         </DialogHeader>

//         <div className="space-y-6">
//           {/* Plan Overview */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Crown className="h-5 w-5 text-yellow-600" />
//                 Current Plan: {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
//               </CardTitle>
//             </CardHeader>
//             <CardContent>
//               <div className="grid grid-cols-4 gap-4">
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(m => m.role === 'admin').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Admins</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(m => m.role !== 'admin').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Team Members</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">{maxTeamSize}</div>
//                   <div className="text-sm text-gray-500">Max Members</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(m => m.status === 'pending').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Pending</div>
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Add New Member */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <UserPlus className="h-5 w-5" />
//                 Add Team Member
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                 <div>
//                   <Label htmlFor="name">Full Name *</Label>
//                   <Input
//                     id="name"
//                     placeholder="John Doe"
//                     value={inviteName}
//                     onChange={(e) => setInviteName(e.target.value)}
//                     disabled={currentTeamCount >= maxTeamSize}
//                   />
//                 </div>
//                 <div>
//                   <Label htmlFor="email">Email Address *</Label>
//                   <Input
//                     id="email"
//                     type="email"
//                     placeholder="john@company.com"
//                     value={inviteEmail}
//                     onChange={(e) => setInviteEmail(e.target.value)}
//                     disabled={currentTeamCount >= maxTeamSize}
//                   />
//                 </div>
//                 <div>
//                   <Label htmlFor="role">Role</Label>
//                   <Select
//                     value={inviteRole}
//                     onValueChange={setInviteRole}
//                     disabled={currentTeamCount >= maxTeamSize}
//                   >
//                     <SelectTrigger>
//                       <SelectValue />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
//                       <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
//                       <SelectItem value="analyst">ESG Analyst</SelectItem>
//                       <SelectItem value="manager">ESG Manager</SelectItem>
//                       <SelectItem value="viewer">Viewer</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>
//               </div>

//               <div className="flex justify-end">
//                 <Button
//                   onClick={addTeamMember}
//                   disabled={loading || !inviteEmail || !inviteName || currentTeamCount >= maxTeamSize}
//                 >
//                   <Mail className="h-4 w-4 mr-2" />
//                   {loading ? 'Sending Invite...' : 'Add & Send Invite'}
//                 </Button>
//               </div>

//               {currentTeamCount >= maxTeamSize && (
//                 <Alert>
//                   <AlertTriangle className="h-4 w-4" />
//                   <AlertDescription>
//                     You have reached the maximum number of team members for your plan. Upgrade to add more.
//                   </AlertDescription>
//                 </Alert>
//               )}

//               <Alert>
//                 <AlertTriangle className="h-4 w-4" />
//                 <AlertDescription>
//                   New users will receive an email invitation to sign up. Existing GreenCard users will be notified that they've been added.
//                 </AlertDescription>
//               </Alert>
//             </CardContent>
//           </Card>

//           {/* Team Members List */}
//           <Card>
//             <CardHeader>
//               <CardTitle>Team Members ({teamMembers.length})</CardTitle>
//             </CardHeader>
//             <CardContent>
//               {teamMembers.length === 0 ? (
//                 <div className="text-center py-8 text-gray-500">
//                   No team members yet. Add your first team member above.
//                 </div>
//               ) : (
//                 <Table>
//                   <TableHeader>
//                     <TableRow>
//                       <TableHead>Member</TableHead>
//                       <TableHead>Email</TableHead>
//                       <TableHead>Role</TableHead>
//                       <TableHead>Status</TableHead>
//                       <TableHead>Joined</TableHead>
//                       <TableHead>Actions</TableHead>
//                     </TableRow>
//                   </TableHeader>
//                   <TableBody>
//                     {teamMembers.map((member) => (
//                       <TableRow key={member.id}>
//                         <TableCell className="font-medium">
//                           <div className="flex items-center gap-2">
//                             {member.profile_picture ? (
//                               <img
//                                 src={member.profile_picture}
//                                 alt={member.full_name}
//                                 className="w-8 h-8 rounded-full"
//                               />
//                             ) : (
//                               <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
//                                 <span className="text-xs font-medium">
//                                   {member.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
//                                 </span>
//                               </div>
//                             )}
//                             <div>
//                               <div>{member.full_name}</div>
//                               {member.role === 'admin' && (
//                                 <Badge variant="outline" className="text-xs">Admin</Badge>
//                               )}
//                             </div>
//                           </div>
//                         </TableCell>
//                         <TableCell>{member.email}</TableCell>
//                         <TableCell>
//                           <Select
//                             value={member.role}
//                             onValueChange={(newRole) => updateMemberRole(member.id, newRole)}
//                             disabled={member.role === 'admin'}
//                           >
//                             <SelectTrigger className="w-40">
//                               <SelectValue>{formatRole(member.role)}</SelectValue>
//                             </SelectTrigger>
//                             <SelectContent>
//                               <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
//                               <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
//                               <SelectItem value="analyst">ESG Analyst</SelectItem>
//                               <SelectItem value="manager">ESG Manager</SelectItem>
//                               <SelectItem value="viewer">Viewer</SelectItem>
//                             </SelectContent>
//                           </Select>
//                         </TableCell>
//                         <TableCell>
//                           <Badge className={getStatusColor(member.status)}>
//                             <div className="flex items-center gap-1">
//                               {getStatusIcon(member.status)}
//                               {member.status}
//                             </div>
//                           </Badge>
//                         </TableCell>
//                         <TableCell>
//                           {member.joined_at
//                             ? new Date(member.joined_at).toLocaleDateString()
//                             : 'Not yet joined'}
//                         </TableCell>
//                         <TableCell>
//                           <div className="flex gap-2">
//                             {member.status === 'pending' && (
//                               <>
//                                 {/* Resend invitation email */}
//                                 <Button
//                                   variant="outline"
//                                   size="sm"
//                                   onClick={() => resendInvitation(member)}
//                                   title="Resend invitation email"
//                                 >
//                                   <Mail className="h-4 w-4" />
//                                 </Button>
//                                 {/* Copy invite link */}
//                                 <Button
//                                   variant="outline"
//                                   size="sm"
//                                   onClick={() => copyInviteLink(member)}
//                                   title="Copy invite link"
//                                 >
//                                   <Copy className="h-4 w-4" />
//                                 </Button>
//                               </>
//                             )}
//                             {member.role !== 'admin' && (
//                               <Button
//                                 variant="outline"
//                                 size="sm"
//                                 onClick={() => removeTeamMember(member.id, member.full_name)}
//                                 className="text-red-600 hover:text-red-700"
//                                 title="Remove member"
//                               >
//                                 <Trash2 className="h-4 w-4" />
//                               </Button>
//                             )}
//                           </div>
//                         </TableCell>
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </Table>
//               )}
//             </CardContent>
//           </Card>
//         </div>

//         <div className="flex justify-end">
//           <Button onClick={onClose}>Close</Button>
//         </div>
//       </DialogContent>
//     </Dialog>
//   );
// };









// import React, { useState, useEffect } from 'react';
// import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Badge } from '@/components/ui/badge';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { useToast } from '@/hooks/use-toast';
// import { useOrganization } from '@/hooks/useOrganization';
// import { 
//   UserPlus, 
//   Mail, 
//   Users, 
//   Crown, 
//   Copy, 
//   CheckCircle,
//   AlertTriangle,
//   Trash2,
//   User
// } from 'lucide-react';
// import { supabase } from '@/integrations/supabase/client';

// interface TeamManagementModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   userPlan: 'individual' | 'corporate' | 'enterprise';
// }

// interface TeamMember {
//   id: string;
//   full_name: string;
//   email: string;
//   role: string;
//   status: string;
//   created_at: string;
//   organization_id: string;
//   user_id?: string;
//   profile_picture?: string;
// }

// export const TeamManagementModal = ({ isOpen, onClose, userPlan }: TeamManagementModalProps) => {
//   const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [inviteEmail, setInviteEmail] = useState('');
//   const [inviteName, setInviteName] = useState('');
//   const [inviteRole, setInviteRole] = useState('sustainability_officer');
//   const { user } = useAuth();
//   const { toast } = useToast();
  
//   const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

//   const getMaxTeamSize = (plan: string = 'individual') => {
//     switch (plan) {
//       case 'individual': return 2;
//       case 'corporate': return 5;
//       case 'enterprise': return 20;
//       default: return 1;
//     }
//   };

//   const maxTeamSize = getMaxTeamSize(userPlan);
//   const currentTeamCount = teamMembers.filter(m => m.status === 'active' || m.status === 'pending').length;

//   const fetchTeamMembers = async () => {
//     if (!organizationId || orgLoading) return;
    
//     try {
//       const { data: members, error } = await supabase
//         .from('team_members')
//         .select('*')
//         .eq('organization_id', organizationId)
//         .order('created_at', { ascending: false });

//       if (error) {
//         console.error('Error fetching team members:', error);
//         toast({
//           title: "Error",
//           description: "Failed to load team members",
//           variant: "destructive"
//         });
//         return;
//       }

//       setTeamMembers(members || []);
//     } catch (error) {
//       console.error('Error in fetchTeamMembers:', error);
//       toast({
//         title: "Error",
//         description: "Failed to load team members",
//         variant: "destructive"
//       });
//     }
//   };

//   useEffect(() => {
//     if (isOpen && organizationId && !orgLoading) {
//       fetchTeamMembers();
//     }
//   }, [isOpen, organizationId, orgLoading]);

//   const addTeamMember = async () => {
//     if (!inviteEmail || !inviteName || !organizationId) return;
    
//     if (currentTeamCount >= maxTeamSize) {
//       toast({
//         title: 'Team Limit Reached',
//         description: `You have reached the maximum number of team members for your plan (${maxTeamSize}).`,
//         variant: 'destructive',
//       });
//       return;
//     }

//     setLoading(true);
//     try {
//       // Check if email already exists in team
//       const existingMember = teamMembers.find(member => 
//         member.email.toLowerCase() === inviteEmail.toLowerCase()
//       );
      
//       if (existingMember) {
//         toast({
//           title: "Member Already Exists",
//           description: `${inviteEmail} is already in your team.`,
//           variant: "destructive"
//         });
//         setLoading(false);
//         return;
//       }

//       // Try to find if user exists in profiles table
//       const { data: existingProfile, error: profileError } = await supabase
//         .from('profiles')
//         .select('id')
//         .eq('email', inviteEmail.toLowerCase())
//         .maybeSingle();

//       // If there's an error other than "no rows", handle it
//       if (profileError && profileError.code !== 'PGRST116') {
//         console.error('Error checking for existing user:', profileError);
//       }

//       const userExists = !!existingProfile;
      
//       // Add to team_members table
//       const { data, error } = await supabase
//         .from('team_members')
//         .insert({
//           organization_id: organizationId,
//           user_id: existingProfile?.id || null,
//           full_name: inviteName,
//           email: inviteEmail.toLowerCase(),
//           role: inviteRole,
//           status: userExists ? 'active' : 'pending',
//           joined_at: userExists ? new Date().toISOString() : null,
//           created_at: new Date().toISOString(),
//           updated_at: new Date().toISOString()
//         })
//         .select()
//         .single();

//       if (error) {
//         if (error.code === '23505') {
//           toast({
//             title: "Already in Team",
//             description: "This email is already associated with your team.",
//             variant: "destructive"
//           });
//         } else {
//           throw error;
//         }
//       } else {
//         toast({
//           title: userExists ? "Team Member Added" : "Invitation Sent",
//           description: userExists 
//             ? `${inviteName} has been added to your team.`
//             : `Invitation sent to ${inviteEmail}. They'll need to sign up first.`,
//         });

//         setInviteEmail('');
//         setInviteName('');
//         fetchTeamMembers();
//       }
      
//     } catch (error: any) {
//       console.error('Error adding team member:', error);
//       toast({
//         title: "Error",
//         description: error.message || "Failed to add team member",
//         variant: "destructive"
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const removeTeamMember = async (memberId: string, memberName: string) => {
//     if (!organizationId || !user) return;

//     try {
//       const memberToRemove = teamMembers.find(m => m.id === memberId);
//       if (memberToRemove?.email === user.email) {
//         toast({
//           title: "Cannot Remove Yourself",
//           description: "You cannot remove yourself as the organization admin.",
//           variant: "destructive"
//         });
//         return;
//       }

//       const { error } = await supabase
//         .from('team_members')
//         .delete()
//         .eq('id', memberId)
//         .eq('organization_id', organizationId);

//       if (error) throw error;

//       toast({
//         title: "Member Removed",
//         description: `${memberName} has been removed from your team.`,
//       });

//       fetchTeamMembers();
//     } catch (error) {
//       console.error('Error removing team member:', error);
//       toast({
//         title: "Error",
//         description: "Failed to remove team member",
//         variant: "destructive"
//       });
//     }
//   };

//   const updateMemberRole = async (memberId: string, newRole: string) => {
//     if (!organizationId) return;

//     try {
//       const member = teamMembers.find(m => m.id === memberId);
//       if (member?.role === 'admin') {
//         toast({
//           title: "Cannot Change Admin Role",
//           description: "Admin role cannot be changed.",
//           variant: "destructive"
//         });
//         return;
//       }

//       const { error } = await supabase
//         .from('team_members')
//         .update({ 
//           role: newRole,
//           updated_at: new Date().toISOString()
//         })
//         .eq('id', memberId)
//         .eq('organization_id', organizationId);

//       if (error) throw error;

//       toast({
//         title: "Role Updated",
//         description: "Team member role has been updated.",
//       });

//       fetchTeamMembers();
//     } catch (error) {
//       console.error('Error updating member role:', error);
//       toast({
//         title: "Error",
//         description: "Failed to update member role",
//         variant: "destructive"
//       });
//     }
//   };

//   const getStatusIcon = (status: string) => {
//     switch (status) {
//       case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
//       default: return <AlertTriangle className="h-4 w-4 text-orange-500" />;
//     }
//   };

//   const getStatusColor = (status: string) => {
//     switch (status) {
//       case 'active': return 'bg-green-100 text-green-800';
//       case 'pending': return 'bg-orange-100 text-orange-800';
//       default: return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const formatRole = (role: string) => {
//     return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
//   };

//   const copyInviteLink = (email: string) => {
//     const inviteToken = Math.random().toString(36).substring(2, 15);
//     const inviteLink = `${window.location.origin}/invite/${inviteToken}?email=${encodeURIComponent(email)}`;
//     navigator.clipboard.writeText(inviteLink);
//     toast({
//       title: "Invite Link Copied",
//       description: "Share this link with the team member to join.",
//     });
//   };

//   if (!isOrganization && !orgLoading) {
//     return (
//       <Dialog open={isOpen} onOpenChange={onClose}>
//         <DialogContent className="max-w-md">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2">
//               <Users className="h-5 w-5" />
//               Team Management
//             </DialogTitle>
//           </DialogHeader>
//           <div className="py-6 text-center">
//             <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
//             <h3 className="text-lg font-semibold mb-2">Individual Account</h3>
//             <p className="text-gray-600 mb-4">
//               Team management is only available for organizations. 
//             </p>
//             {/* <p className="text-gray-600 mb-4">
//               Team management is only available for organizations. 
//               Complete your company profile to enable team features.
//             </p> */}
//             <Button onClick={onClose} className="w-full">
//               Close
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//     );
//   }

//   return (
//     <Dialog open={isOpen} onOpenChange={onClose}>
//       <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <Users className="h-5 w-5" />
//             Team Management
//           </DialogTitle>
//           <DialogDescription>
//             Manage your sustainability team members and collaboration access
//           </DialogDescription>
//         </DialogHeader>

//         <div className="space-y-6">
//           {/* Plan Overview */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Crown className="h-5 w-5 text-yellow-600" />
//                 Current Plan: {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
//               </CardTitle>
//             </CardHeader>
//             <CardContent>
//               <div className="grid grid-cols-4 gap-4">
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(member => member.role === 'admin').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Admins</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(member => member.role !== 'admin').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Team Members</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {maxTeamSize}
//                   </div>
//                   <div className="text-sm text-gray-500">Max Members</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">
//                     {teamMembers.filter(member => member.status === 'pending').length}
//                   </div>
//                   <div className="text-sm text-gray-500">Pending</div>
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Add New Member */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <UserPlus className="h-5 w-5" />
//                 Add Team Member
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                 <div>
//                   <Label htmlFor="name">Full Name *</Label>
//                   <Input
//                     id="name"
//                     placeholder="John Doe"
//                     value={inviteName}
//                     onChange={(e) => setInviteName(e.target.value)}
//                     disabled={currentTeamCount >= maxTeamSize}
//                   />
//                 </div>
//                 <div>
//                   <Label htmlFor="email">Email Address *</Label>
//                   <Input
//                     id="email"
//                     type="email"
//                     placeholder="john@company.com"
//                     value={inviteEmail}
//                     onChange={(e) => setInviteEmail(e.target.value)}
//                     disabled={currentTeamCount >= maxTeamSize}
//                   />
//                 </div>
//                 <div>
//                   <Label htmlFor="role">Role</Label>
//                   <Select 
//                     value={inviteRole} 
//                     onValueChange={setInviteRole} 
//                     disabled={currentTeamCount >= maxTeamSize}
//                   >
//                     <SelectTrigger>
//                       <SelectValue />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
//                       <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
//                       <SelectItem value="analyst">ESG Analyst</SelectItem>
//                       <SelectItem value="manager">ESG Manager</SelectItem>
//                       <SelectItem value="viewer">Viewer</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>
//               </div>
              
//               <div className="flex justify-end">
//                 <Button 
//                   onClick={addTeamMember} 
//                   disabled={loading || !inviteEmail || !inviteName || currentTeamCount >= maxTeamSize}
//                 >
//                   <Mail className="h-4 w-4 mr-2" />
//                   {loading ? 'Adding...' : 'Add to Team'}
//                 </Button>
//               </div>
              
//               {currentTeamCount >= maxTeamSize && (
//                 <Alert>
//                   <AlertTriangle className="h-4 w-4" />
//                   <AlertDescription>
//                     You have reached the maximum number of team members for your plan. Upgrade your plan to add more members.
//                   </AlertDescription>
//                 </Alert>
//               )}

//               <Alert>
//                 <AlertTriangle className="h-4 w-4" />
//                 <AlertDescription>
//                   Team members will have access to shared data based on their role. 
//                   Users who haven't signed up yet will receive an invitation.
//                 </AlertDescription>
//               </Alert>
//             </CardContent>
//           </Card>

//           {/* Team Members List */}
//           <Card>
//             <CardHeader>
//               <CardTitle>Team Members ({teamMembers.length})</CardTitle>
//             </CardHeader>
//             <CardContent>
//               {teamMembers.length === 0 ? (
//                 <div className="text-center py-8 text-gray-500">
//                   No team members yet. Add your first team member above.
//                 </div>
//               ) : (
//                 <Table>
//                   <TableHeader>
//                     <TableRow>
//                       <TableHead>Member</TableHead>
//                       <TableHead>Email</TableHead>
//                       <TableHead>Role</TableHead>
//                       <TableHead>Status</TableHead>
//                       <TableHead>Joined</TableHead>
//                       <TableHead>Actions</TableHead>
//                     </TableRow>
//                   </TableHeader>
//                   <TableBody>
//                     {teamMembers.map((member) => (
//                       <TableRow key={member.id}>
//                         <TableCell className="font-medium">
//                           <div className="flex items-center gap-2">
//                             {member.profile_picture ? (
//                               <img 
//                                 src={member.profile_picture} 
//                                 alt={member.full_name}
//                                 className="w-8 h-8 rounded-full"
//                               />
//                             ) : (
//                               <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
//                                 <span className="text-xs font-medium">
//                                   {member.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
//                                 </span>
//                               </div>
//                             )}
//                             <div>
//                               <div>{member.full_name}</div>
//                               {member.role === 'admin' && (
//                                 <Badge variant="outline" className="text-xs">
//                                   Admin
//                                 </Badge>
//                               )}
//                             </div>
//                           </div>
//                         </TableCell>
//                         <TableCell>{member.email}</TableCell>
//                         <TableCell>
//                           <Select
//                             value={member.role}
//                             onValueChange={(newRole) => updateMemberRole(member.id, newRole)}
//                             disabled={member.role === 'admin'}
//                           >
//                             <SelectTrigger className="w-40">
//                               <SelectValue>{formatRole(member.role)}</SelectValue>
//                             </SelectTrigger>
//                             <SelectContent>
//                               <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
//                               <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
//                               <SelectItem value="analyst">ESG Analyst</SelectItem>
//                               <SelectItem value="manager">ESG Manager</SelectItem>
//                               <SelectItem value="viewer">Viewer</SelectItem>
//                             </SelectContent>
//                           </Select>
//                         </TableCell>
//                         <TableCell>
//                           <Badge className={getStatusColor(member.status)}>
//                             <div className="flex items-center gap-1">
//                               {getStatusIcon(member.status)}
//                               {member.status}
//                             </div>
//                           </Badge>
//                         </TableCell>
//                         <TableCell>
//                           {member.joined_at 
//                             ? new Date(member.joined_at).toLocaleDateString()
//                             : 'Not yet joined'
//                           }
//                         </TableCell>
//                         <TableCell>
//                           <div className="flex gap-2">
//                             {member.status === 'pending' && (
//                               <Button
//                                 variant="outline"
//                                 size="sm"
//                                 onClick={() => copyInviteLink(member.email)}
//                                 title="Copy invite link"
//                               >
//                                 <Copy className="h-4 w-4" />
//                               </Button>
//                             )}
//                             {member.role !== 'admin' && (
//                               <Button
//                                 variant="outline"
//                                 size="sm"
//                                 onClick={() => removeTeamMember(member.id, member.full_name)}
//                                 className="text-red-600 hover:text-red-700"
//                                 title="Remove member"
//                               >
//                                 <Trash2 className="h-4 w-4" />
//                               </Button>
//                             )}
//                           </div>
//                         </TableCell>
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </Table>
//               )}
//             </CardContent>
//           </Card>
//         </div>

//         <div className="flex justify-end">
//           <Button onClick={onClose}>Close</Button>
//         </div>
//       </DialogContent>
//     </Dialog>
//   );
// };





// // import React, { useState, useEffect } from 'react';
// // import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// // import { Button } from '@/components/ui/button';
// // import { Input } from '@/components/ui/input';
// // import { Label } from '@/components/ui/label';
// // import { Badge } from '@/components/ui/badge';
// // import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// // import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// // import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// // import { Alert, AlertDescription } from '@/components/ui/alert';
// // import { useAuth } from '@/components/Auth/AuthProvider';
// // import { useToast } from '@/hooks/use-toast';
// // import { useOrganization } from '@/hooks/useOrganization'; // Import the hook
// // import { 
// //   UserPlus, 
// //   Mail, 
// //   Users, 
// //   Crown, 
// //   Copy, 
// //   CheckCircle,
// //   AlertTriangle,
// //   Trash2,
// //   User
// // } from 'lucide-react';
// // import { supabase } from '@/integrations/supabase/client';

// // interface TeamManagementModalProps {
// //   isOpen: boolean;
// //   onClose: () => void;
// //   userPlan: 'individual' | 'corporate' | 'enterprise';
// // }

// // interface TeamMember {
// //   id: string;
// //   full_name: string;
// //   email: string;
// //   role: string;
// //   status: string;
// //   created_at: string;
// //   organization_id: string;
// //   user_id?: string;
// //   profile_picture?: string;
// // }

// // export const TeamManagementModal = ({ isOpen, onClose, userPlan }: TeamManagementModalProps) => {
// //   const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
// //   const [loading, setLoading] = useState(false);
// //   const [inviteEmail, setInviteEmail] = useState('');
// //   const [inviteName, setInviteName] = useState('');
// //   const [inviteRole, setInviteRole] = useState('sustainability_officer');
// //   const { user } = useAuth();
// //   const { toast } = useToast();
  
// //   // Use the organization hook
// //   const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

// //   const getMaxTeamSize = (plan: string = 'individual') => {
// //     switch (plan) {
// //       case 'individual': return 2; // changed to 2
// //       case 'corporate': return 5; // changed to 5
// //       case 'enterprise': return 20; // changed to 20
// //       default: return 1;
// //     }
// //   };

// //   const maxTeamSize = getMaxTeamSize(userPlan);
// //   const currentTeamCount = teamMembers.filter(m => m.status === 'active' || m.status === 'pending').length;

// //   // Fetch team members from team_members table
// //   const fetchTeamMembers = async () => {
// //     if (!organizationId || orgLoading) return;
    
// //     try {
// //       // Fetch team members for current organization
// //       const { data: members, error } = await supabase
// //         .from('team_members')
// //         .select('*')
// //         .eq('organization_id', organizationId)
// //         .order('created_at', { ascending: false });

// //       if (error) {
// //         console.error('Error fetching team members:', error);
// //         toast({
// //           title: "Error",
// //           description: "Failed to load team members",
// //           variant: "destructive"
// //         });
// //         return;
// //       }

// //       setTeamMembers(members || []);
// //     } catch (error) {
// //       console.error('Error in fetchTeamMembers:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to load team members",
// //         variant: "destructive"
// //       });
// //     }
// //   };

// //   useEffect(() => {
// //     if (isOpen && organizationId && !orgLoading) {
// //       fetchTeamMembers();
// //     }
// //   }, [isOpen, organizationId, orgLoading]);

// //   const addTeamMember = async () => {
// //     if (!inviteEmail || !inviteName || !organizationId) return;
    
// //     if (currentTeamCount >= maxTeamSize) {
// //       toast({
// //         title: 'Team Limit Reached',
// //         description: `You have reached the maximum number of team members for your plan (${maxTeamSize}).`,
// //         variant: 'destructive',
// //       });
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       // Check if email already exists in team
// //       const existingMember = teamMembers.find(member => 
// //         member.email.toLowerCase() === inviteEmail.toLowerCase()
// //       );
      
// //       if (existingMember) {
// //         toast({
// //           title: "Member Already Exists",
// //           description: `${inviteEmail} is already in your team.`,
// //           variant: "destructive"
// //         });
// //         setLoading(false);
// //         return;
// //       }

// //       // Check if user exists in auth
// //       const { data: existingUser } = await supabase.auth.admin.getUserByEmail(inviteEmail);
      
// //       const userExists = !!existingUser.user;
      
// //       // Add to team_members table
// //       const { data, error } = await supabase
// //         .from('team_members')
// //         .insert({
// //           organization_id: organizationId,
// //           user_id: existingUser.user?.id || null, // Link if user exists
// //           full_name: inviteName,
// //           email: inviteEmail.toLowerCase(),
// //           role: inviteRole,
// //           status: userExists ? 'active' : 'pending',
// //           joined_at: userExists ? new Date().toISOString() : null,
// //           created_at: new Date().toISOString(),
// //           updated_at: new Date().toISOString()
// //         })
// //         .select()
// //         .single();

// //       if (error) {
// //         if (error.code === '23505') { // Unique constraint violation
// //           toast({
// //             title: "Already in Team",
// //             description: "This email is already associated with your team.",
// //             variant: "destructive"
// //           });
// //         } else {
// //           throw error;
// //         }
// //       } else {
// //         toast({
// //           title: userExists ? "Team Member Added" : "Invitation Sent",
// //           description: userExists 
// //             ? `${inviteName} has been added to your team.`
// //             : `Invitation sent to ${inviteEmail}. They'll need to sign up first.`,
// //         });

// //         // Reset form and refresh
// //         setInviteEmail('');
// //         setInviteName('');
// //         fetchTeamMembers();
// //       }
      
// //     } catch (error: any) {
// //       console.error('Error adding team member:', error);
// //       toast({
// //         title: "Error",
// //         description: error.message || "Failed to add team member",
// //         variant: "destructive"
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const removeTeamMember = async (memberId: string, memberName: string) => {
// //     if (!organizationId || !user) return;

// //     try {
// //       // Prevent admin from removing themselves
// //       const memberToRemove = teamMembers.find(m => m.id === memberId);
// //       if (memberToRemove?.email === user.email) {
// //         toast({
// //           title: "Cannot Remove Yourself",
// //           description: "You cannot remove yourself as the organization admin.",
// //           variant: "destructive"
// //         });
// //         return;
// //       }

// //       const { error } = await supabase
// //         .from('team_members')
// //         .delete()
// //         .eq('id', memberId)
// //         .eq('organization_id', organizationId);

// //       if (error) throw error;

// //       toast({
// //         title: "Member Removed",
// //         description: `${memberName} has been removed from your team.`,
// //       });

// //       fetchTeamMembers(); // Refresh the list
// //     } catch (error) {
// //       console.error('Error removing team member:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to remove team member",
// //         variant: "destructive"
// //       });
// //     }
// //   };

// //   const updateMemberRole = async (memberId: string, newRole: string) => {
// //     if (!organizationId) return;

// //     try {
// //       // Prevent changing admin role
// //       const member = teamMembers.find(m => m.id === memberId);
// //       if (member?.role === 'admin') {
// //         toast({
// //           title: "Cannot Change Admin Role",
// //           description: "Admin role cannot be changed.",
// //           variant: "destructive"
// //         });
// //         return;
// //       }

// //       const { error } = await supabase
// //         .from('team_members')
// //         .update({ 
// //           role: newRole,
// //           updated_at: new Date().toISOString()
// //         })
// //         .eq('id', memberId)
// //         .eq('organization_id', organizationId);

// //       if (error) throw error;

// //       toast({
// //         title: "Role Updated",
// //         description: "Team member role has been updated.",
// //       });

// //       fetchTeamMembers(); // Refresh the list
// //     } catch (error) {
// //       console.error('Error updating member role:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to update member role",
// //         variant: "destructive"
// //       });
// //     }
// //   };

// //   const getStatusIcon = (status: string) => {
// //     switch (status) {
// //       case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
// //       default: return <AlertTriangle className="h-4 w-4 text-orange-500" />;
// //     }
// //   };

// //   const getStatusColor = (status: string) => {
// //     switch (status) {
// //       case 'active': return 'bg-green-100 text-green-800';
// //       case 'pending': return 'bg-orange-100 text-orange-800';
// //       default: return 'bg-gray-100 text-gray-800';
// //     }
// //   };

// //   const formatRole = (role: string) => {
// //     return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
// //   };

// //   const copyInviteLink = (email: string) => {
// //     const inviteToken = Math.random().toString(36).substring(2, 15);
// //     const inviteLink = `${window.location.origin}/invite/${inviteToken}?email=${encodeURIComponent(email)}`;
// //     navigator.clipboard.writeText(inviteLink);
// //     toast({
// //       title: "Invite Link Copied",
// //       description: "Share this link with the team member to join.",
// //     });
// //   };

// //   // If user is not an organization, show message
// //   if (!isOrganization && !orgLoading) {
// //     return (
// //       <Dialog open={isOpen} onOpenChange={onClose}>
// //         <DialogContent className="max-w-md">
// //           <DialogHeader>
// //             <DialogTitle className="flex items-center gap-2">
// //               <Users className="h-5 w-5" />
// //               Team Management
// //             </DialogTitle>
// //           </DialogHeader>
// //           <div className="py-6 text-center">
// //             <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
// //             <h3 className="text-lg font-semibold mb-2">Individual Account</h3>
// //             <p className="text-gray-600 mb-4">
// //               Team management is only available for organizations. 
// //               Complete your company profile to enable team features.
// //             </p>
// //             <Button onClick={onClose} className="w-full">
// //               Close
// //             </Button>
// //           </div>
// //         </DialogContent>
// //       </Dialog>
// //     );
// //   }

// //   return (
// //     <Dialog open={isOpen} onOpenChange={onClose}>
// //       <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
// //         <DialogHeader>
// //           <DialogTitle className="flex items-center gap-2">
// //             <Users className="h-5 w-5" />
// //             Team Management
// //           </DialogTitle>
// //           <DialogDescription>
// //             Manage your sustainability team members and collaboration access
// //           </DialogDescription>
// //         </DialogHeader>

// //         <div className="space-y-6">
// //           {/* Plan Overview */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle className="flex items-center gap-2">
// //                 <Crown className="h-5 w-5 text-yellow-600" />
// //                 Current Plan: {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
// //               </CardTitle>
// //             </CardHeader>
// //             <CardContent>
// //               <div className="grid grid-cols-4 gap-4">
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {teamMembers.filter(member => member.role === 'admin').length}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Admins</div>
// //                 </div>
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {teamMembers.filter(member => member.role !== 'admin').length}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Team Members</div>
// //                 </div>
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {maxTeamSize}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Max Members</div>
// //                 </div>
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {teamMembers.filter(member => member.status === 'pending').length}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Pending</div>
// //                 </div>
// //               </div>
// //             </CardContent>
// //           </Card>

// //           {/* Add New Member */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle className="flex items-center gap-2">
// //                 <UserPlus className="h-5 w-5" />
// //                 Add Team Member
// //               </CardTitle>
// //             </CardHeader>
// //             <CardContent className="space-y-4">
// //               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
// //                 <div>
// //                   <Label htmlFor="name">Full Name *</Label>
// //                   <Input
// //                     id="name"
// //                     placeholder="John Doe"
// //                     value={inviteName}
// //                     onChange={(e) => setInviteName(e.target.value)}
// //                     disabled={currentTeamCount >= maxTeamSize}
// //                   />
// //                 </div>
// //                 <div>
// //                   <Label htmlFor="email">Email Address *</Label>
// //                   <Input
// //                     id="email"
// //                     type="email"
// //                     placeholder="john@company.com"
// //                     value={inviteEmail}
// //                     onChange={(e) => setInviteEmail(e.target.value)}
// //                     disabled={currentTeamCount >= maxTeamSize}
// //                   />
// //                 </div>
// //                 <div>
// //                   <Label htmlFor="role">Role</Label>
// //                   <Select 
// //                     value={inviteRole} 
// //                     onValueChange={setInviteRole} 
// //                     disabled={currentTeamCount >= maxTeamSize}
// //                   >
// //                     <SelectTrigger>
// //                       <SelectValue />
// //                     </SelectTrigger>
// //                     <SelectContent>
// //                       <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
// //                       <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
// //                       <SelectItem value="analyst">ESG Analyst</SelectItem>
// //                       <SelectItem value="manager">ESG Manager</SelectItem>
// //                       <SelectItem value="viewer">Viewer</SelectItem>
// //                     </SelectContent>
// //                   </Select>
// //                 </div>
// //               </div>
              
// //               <div className="flex justify-end">
// //                 <Button 
// //                   onClick={addTeamMember} 
// //                   disabled={loading || !inviteEmail || !inviteName || currentTeamCount >= maxTeamSize}
// //                 >
// //                   <Mail className="h-4 w-4 mr-2" />
// //                   {loading ? 'Adding...' : 'Add to Team'}
// //                 </Button>
// //               </div>
              
// //               {currentTeamCount >= maxTeamSize && (
// //                 <Alert>
// //                   <AlertTriangle className="h-4 w-4" />
// //                   <AlertDescription>
// //                     You have reached the maximum number of team members for your plan. Upgrade your plan to add more members.
// //                   </AlertDescription>
// //                 </Alert>
// //               )}

// //               <Alert>
// //                 <AlertTriangle className="h-4 w-4" />
// //                 <AlertDescription>
// //                   Team members will have access to shared data based on their role. 
// //                   Users who haven't signed up yet will receive an invitation.
// //                 </AlertDescription>
// //               </Alert>
// //             </CardContent>
// //           </Card>

// //           {/* Team Members List */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle>Team Members ({teamMembers.length})</CardTitle>
// //             </CardHeader>
// //             <CardContent>
// //               {teamMembers.length === 0 ? (
// //                 <div className="text-center py-8 text-gray-500">
// //                   No team members yet. Add your first team member above.
// //                 </div>
// //               ) : (
// //                 <Table>
// //                   <TableHeader>
// //                     <TableRow>
// //                       <TableHead>Member</TableHead>
// //                       <TableHead>Email</TableHead>
// //                       <TableHead>Role</TableHead>
// //                       <TableHead>Status</TableHead>
// //                       <TableHead>Joined</TableHead>
// //                       <TableHead>Actions</TableHead>
// //                     </TableRow>
// //                   </TableHeader>
// //                   <TableBody>
// //                     {teamMembers.map((member) => (
// //                       <TableRow key={member.id}>
// //                         <TableCell className="font-medium">
// //                           <div className="flex items-center gap-2">
// //                             {member.profile_picture ? (
// //                               <img 
// //                                 src={member.profile_picture} 
// //                                 alt={member.full_name}
// //                                 className="w-8 h-8 rounded-full"
// //                               />
// //                             ) : (
// //                               <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
// //                                 <span className="text-xs font-medium">
// //                                   {member.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
// //                                 </span>
// //                               </div>
// //                             )}
// //                             <div>
// //                               <div>{member.full_name}</div>
// //                               {member.role === 'admin' && (
// //                                 <Badge variant="outline" className="text-xs">
// //                                   Admin
// //                                 </Badge>
// //                               )}
// //                             </div>
// //                           </div>
// //                         </TableCell>
// //                         <TableCell>{member.email}</TableCell>
// //                         <TableCell>
// //                           <Select
// //                             value={member.role}
// //                             onValueChange={(newRole) => updateMemberRole(member.id, newRole)}
// //                             disabled={member.role === 'admin'}
// //                           >
// //                             <SelectTrigger className="w-40">
// //                               <SelectValue>{formatRole(member.role)}</SelectValue>
// //                             </SelectTrigger>
// //                             <SelectContent>
// //                               <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
// //                               <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
// //                               <SelectItem value="analyst">ESG Analyst</SelectItem>
// //                               <SelectItem value="manager">ESG Manager</SelectItem>
// //                               <SelectItem value="viewer">Viewer</SelectItem>
// //                             </SelectContent>
// //                           </Select>
// //                         </TableCell>
// //                         <TableCell>
// //                           <Badge className={getStatusColor(member.status)}>
// //                             <div className="flex items-center gap-1">
// //                               {getStatusIcon(member.status)}
// //                               {member.status}
// //                             </div>
// //                           </Badge>
// //                         </TableCell>
// //                         <TableCell>
// //                           {member.joined_at 
// //                             ? new Date(member.joined_at).toLocaleDateString()
// //                             : 'Not yet joined'
// //                           }
// //                         </TableCell>
// //                         <TableCell>
// //                           <div className="flex gap-2">
// //                             {member.status === 'pending' && (
// //                               <Button
// //                                 variant="outline"
// //                                 size="sm"
// //                                 onClick={() => copyInviteLink(member.email)}
// //                                 title="Copy invite link"
// //                               >
// //                                 <Copy className="h-4 w-4" />
// //                               </Button>
// //                             )}
// //                             {member.role !== 'admin' && (
// //                               <Button
// //                                 variant="outline"
// //                                 size="sm"
// //                                 onClick={() => removeTeamMember(member.id, member.full_name)}
// //                                 className="text-red-600 hover:text-red-700"
// //                                 title="Remove member"
// //                               >
// //                                 <Trash2 className="h-4 w-4" />
// //                               </Button>
// //                             )}
// //                           </div>
// //                         </TableCell>
// //                       </TableRow>
// //                     ))}
// //                   </TableBody>
// //                 </Table>
// //               )}
// //             </CardContent>
// //           </Card>
// //         </div>

// //         <div className="flex justify-end">
// //           <Button onClick={onClose}>Close</Button>
// //         </div>
// //       </DialogContent>
// //     </Dialog>
// //   );
// // };






// // import React, { useState, useEffect } from 'react';
// // import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// // import { Button } from '@/components/ui/button';
// // import { Input } from '@/components/ui/input';
// // import { Label } from '@/components/ui/label';
// // import { Badge } from '@/components/ui/badge';
// // import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// // import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// // import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// // import { Alert, AlertDescription } from '@/components/ui/alert';
// // import { useAuth } from '@/components/Auth/AuthProvider';
// // import { useToast } from '@/hooks/use-toast';
// // import { 
// //   UserPlus, 
// //   Mail, 
// //   Users, 
// //   Crown, 
// //   Copy, 
// //   CheckCircle,
// //   AlertTriangle
// // } from 'lucide-react';
// // import { supabase } from '@/integrations/supabase/client';

// // interface TeamManagementModalProps {
// //   isOpen: boolean;
// //   onClose: () => void;
// //   userPlan: 'individual' | 'corporate' | 'enterprise';
// // }

// // interface TeamMember {
// //   id: string;
// //   full_name: string;
// //   email: string;
// //   role: string;
// //   status: string;
// //   created_at: string;
// //   organization_id?: string;
// //   user_id?: string;
// // }

// // export const TeamManagementModal = ({ isOpen, onClose, userPlan }: TeamManagementModalProps) => {
// //   const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
// //   const [loading, setLoading] = useState(false);
// //   const [inviteEmail, setInviteEmail] = useState('');
// //   const [inviteRole, setInviteRole] = useState('sustainability_officer');
// //   const [organizationId, setOrganizationId] = useState<string | null>(null);
// //   const { user } = useAuth();
// //   const { toast } = useToast();

// //   const getMaxTeamSize = (plan: string = 'individual') => {
// //     switch (plan) {
// //       case 'individual': return 1;
// //       case 'corporate': return 3;
// //       case 'enterprise': return 10;
// //       default: return 1;
// //     }
// //   };

// //   const maxTeamSize = getMaxTeamSize(userPlan);
// //   const currentTeamCount = teamMembers.filter(m => m.status === 'active' || m.status === 'pending').length;

// //   // Fetch team members from database
// //   const fetchTeamMembers = async () => {
// //     if (!user?.id) return;
    
// //     try {
// //       // First, get the user's organization
// //       const { data: orgData, error: orgError } = await supabase
// //         .from('organizations')
// //         .select('id')
// //         .eq('admin_user_id', user.id)
// //         .single();

// //       if (orgError && orgError.code !== 'PGRST116') {
// //         console.error('Error fetching organization:', orgError);
// //         return;
// //       }

// //       if (orgData) {
// //         setOrganizationId(orgData.id);
        
// //         // Fetch organization members
// //         const { data: members, error: membersError } = await supabase
// //           .from('organization_members')
// //           .select(`
// //             id,
// //             role,
// //             status,
// //             created_at,
// //             user_id,
// //             users:user_id (
// //               email,
// //               raw_user_meta_data
// //             )
// //           `)
// //           .eq('organization_id', orgData.id);

// //         if (membersError) {
// //           console.error('Error fetching team members:', membersError);
// //           return;
// //         }

// //         const formattedMembers: TeamMember[] = (members || []).map(member => ({
// //           id: member.id,
// //           full_name: member.users?.raw_user_meta_data?.full_name || member.users?.email?.split('@')[0] || 'Unknown',
// //           email: member.users?.email || 'Unknown',
// //           role: member.role,
// //           status: member.status,
// //           created_at: member.created_at,
// //           organization_id: orgData.id,
// //           user_id: member.user_id
// //         }));

// //         setTeamMembers(formattedMembers);
// //       }
// //     } catch (error) {
// //       console.error('Error in fetchTeamMembers:', error);
// //     }
// //   };

// //   useEffect(() => {
// //     if (isOpen && user) {
// //       fetchTeamMembers();
// //     }
// //   }, [isOpen, user]);

// //   const sendInvitation = async () => {
// //     if (!inviteEmail || !user || !organizationId) return;
// //     if (currentTeamCount >= maxTeamSize) {
// //       toast({
// //         title: 'Team Limit Reached',
// //         description: `You have reached the maximum number of team members for your plan (${maxTeamSize}).`,
// //         variant: 'destructive',
// //       });
// //       return;
// //     }

// //     setLoading(true);
// //     try {
// //       // Check if user already exists
// //       const { data: existingUser } = await supabase.auth.admin.getUserByEmail(inviteEmail);
      
// //       if (existingUser.user) {
// //         // User exists, add them directly to organization
// //         const { error: memberError } = await supabase
// //           .from('organization_members')
// //           .insert({
// //             organization_id: organizationId,
// //             user_id: existingUser.user.id,
// //             role: inviteRole,
// //             status: 'active',
// //             invited_by: user.id
// //           });

// //         if (memberError) {
// //           if (memberError.code === '23505') { // Unique constraint violation
// //             toast({
// //               title: "User Already in Team",
// //               description: "This user is already a member of your organization.",
// //               variant: "destructive"
// //             });
// //           } else {
// //             throw memberError;
// //           }
// //         } else {
// //           toast({
// //             title: "Team Member Added",
// //             description: `${inviteEmail} has been added to your team.`,
// //           });
// //           fetchTeamMembers(); // Refresh the list
// //         }
// //       } else {
// //         // User doesn't exist, create invitation
// //         const { error: inviteError } = await supabase
// //           .from('organization_members')
// //           .insert({
// //             organization_id: organizationId,
// //             user_id: null, // Will be set when user signs up
// //             role: inviteRole,
// //             status: 'pending',
// //             invited_by: user.id
// //           });

// //         if (inviteError) {
// //           throw inviteError;
// //         }

// //         // Generate invitation link
// //         const invitationToken = Math.random().toString(36).substring(2, 15);
// //         const invitationLink = `${window.location.origin}/invite/${invitationToken}`;
        
// //         // Copy to clipboard
// //         navigator.clipboard.writeText(invitationLink);

// //         toast({
// //           title: "Invitation Created",
// //           description: `Invitation link copied to clipboard. Share this link with ${inviteEmail}`,
// //         });

// //         fetchTeamMembers(); // Refresh the list
// //       }

// //       setInviteEmail('');
// //     } catch (error) {
// //       console.error('Error sending invitation:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to create invitation",
// //         variant: "destructive"
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   const removeTeamMember = async (memberId: string, memberEmail: string) => {
// //     if (!user || !organizationId) return;

// //     try {
// //       const { error } = await supabase
// //         .from('organization_members')
// //         .delete()
// //         .eq('id', memberId)
// //         .eq('organization_id', organizationId);

// //       if (error) {
// //         throw error;
// //       }

// //       toast({
// //         title: "Member Removed",
// //         description: `${memberEmail} has been removed from your team.`,
// //       });

// //       fetchTeamMembers(); // Refresh the list
// //     } catch (error) {
// //       console.error('Error removing team member:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to remove team member",
// //         variant: "destructive"
// //       });
// //     }
// //   };

// //   const updateMemberRole = async (memberId: string, newRole: string) => {
// //     if (!user || !organizationId) return;

// //     try {
// //       const { error } = await supabase
// //         .from('organization_members')
// //         .update({ role: newRole })
// //         .eq('id', memberId)
// //         .eq('organization_id', organizationId);

// //       if (error) {
// //         throw error;
// //       }

// //       toast({
// //         title: "Role Updated",
// //         description: "Team member role has been updated.",
// //       });

// //       fetchTeamMembers(); // Refresh the list
// //     } catch (error) {
// //       console.error('Error updating member role:', error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to update member role",
// //         variant: "destructive"
// //       });
// //     }
// //   };

// //   const getStatusIcon = (status: string) => {
// //     switch (status) {
// //       case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
// //       default: return <AlertTriangle className="h-4 w-4 text-orange-500" />;
// //     }
// //   };

// //   const getStatusColor = (status: string) => {
// //     switch (status) {
// //       case 'active': return 'bg-green-100 text-green-800';
// //       default: return 'bg-orange-100 text-orange-800';
// //     }
// //   };

// //   const formatRole = (role: string) => {
// //     return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
// //   };

// //   return (
// //     <Dialog open={isOpen} onOpenChange={onClose}>
// //       <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
// //         <DialogHeader>
// //           <DialogTitle className="flex items-center gap-2">
// //             <Users className="h-5 w-5" />
// //             Team Management
// //           </DialogTitle>
// //           <DialogDescription>
// //             Manage your sustainability team members and collaboration access
// //           </DialogDescription>
// //         </DialogHeader>

// //         <div className="space-y-6">
// //           {/* Plan Overview */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle className="flex items-center gap-2">
// //                 <Crown className="h-5 w-5 text-yellow-600" />
// //                 Current Plan: {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}
// //               </CardTitle>
// //             </CardHeader>
// //             <CardContent>
// //               <div className="grid grid-cols-3 gap-4">
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {teamMembers.filter(member => member.status === 'active').length}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Active Members</div>
// //                 </div>
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {getMaxTeamSize(userPlan)}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Max Members</div>
// //                 </div>
// //                 <div className="text-center">
// //                   <div className="text-2xl font-bold">
// //                     {teamMembers.filter(member => member.status === 'pending').length}
// //                   </div>
// //                   <div className="text-sm text-gray-500">Pending Invites</div>
// //                 </div>
// //               </div>
// //             </CardContent>
// //           </Card>

// //           {/* Invite New Member */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle className="flex items-center gap-2">
// //                 <UserPlus className="h-5 w-5" />
// //                 Invite Team Member
// //               </CardTitle>
// //             </CardHeader>
// //             <CardContent className="space-y-4">
// //               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
// //                 <div>
// //                   <Label htmlFor="email">Email Address</Label>
// //                   <Input
// //                     id="email"
// //                     type="email"
// //                     placeholder="colleague@company.com"
// //                     value={inviteEmail}
// //                     onChange={(e) => setInviteEmail(e.target.value)}
// //                     disabled={currentTeamCount >= maxTeamSize}
// //                   />
// //                 </div>
// //                 <div>
// //                   <Label htmlFor="role">Role</Label>
// //                   <Select value={inviteRole} onValueChange={setInviteRole} disabled={currentTeamCount >= maxTeamSize}>
// //                     <SelectTrigger>
// //                       <SelectValue />
// //                     </SelectTrigger>
// //                     <SelectContent>
// //                       <SelectItem value="sustainability_officer">Sustainability Officer</SelectItem>
// //                       <SelectItem value="data_entry">Data Entry Specialist</SelectItem>
// //                       <SelectItem value="analyst">ESG Analyst</SelectItem>
// //                       <SelectItem value="manager">ESG Manager</SelectItem>
// //                     </SelectContent>
// //                   </Select>
// //                 </div>
// //                 <div className="flex items-end">
// //                   <Button 
// //                     onClick={sendInvitation} 
// //                     disabled={loading || !inviteEmail || currentTeamCount >= maxTeamSize}
// //                     className="w-full"
// //                   >
// //                     <Mail className="h-4 w-4 mr-2" />
// //                     Send Invitation
// //                   </Button>
// //                 </div>
// //               </div>
// //               {currentTeamCount >= maxTeamSize && (
// //                 <Alert>
// //                   <AlertTriangle className="h-4 w-4" />
// //                   <AlertDescription>
// //                     You have reached the maximum number of team members for your plan. Upgrade your plan to add more members.
// //                   </AlertDescription>
// //                 </Alert>
// //               )}

// //               <Alert>
// //                 <AlertTriangle className="h-4 w-4" />
// //                 <AlertDescription>
// //                   Team members will receive a unique signup link to join your organization's account. 
// //                   They'll have access to shared data and collaborative features based on their role.
// //                 </AlertDescription>
// //               </Alert>
// //             </CardContent>
// //           </Card>

// //           {/* Team Members */}
// //           <Card>
// //             <CardHeader>
// //               <CardTitle>Team Members</CardTitle>
// //             </CardHeader>
// //             <CardContent>
// //               {teamMembers.length === 0 ? (
// //                 <div className="text-center py-8 text-gray-500">
// //                   No team members yet
// //                 </div>
// //               ) : (
// //                 <Table>
// //                   <TableHeader>
// //                     <TableRow>
// //                       <TableHead>Name</TableHead>
// //                       <TableHead>Email</TableHead>
// //                       <TableHead>Role</TableHead>
// //                       <TableHead>Status</TableHead>
// //                       <TableHead>Joined</TableHead>
// //                       <TableHead>Actions</TableHead>
// //                     </TableRow>
// //                   </TableHeader>
// //                   <TableBody>
// //                     {teamMembers.map((member) => (
// //                       <TableRow key={member.id}>
// //                         <TableCell className="font-medium">
// //                           {member.full_name}
// //                         </TableCell>
// //                         <TableCell>{member.email}</TableCell>
// //                         <TableCell>
// //                           <Select
// //                             value={member.role}
// //                             onValueChange={(newRole) => updateMemberRole(member.id, newRole)}
// //                             disabled={member.status === 'pending'}
// //                           >
// //                             <SelectTrigger className="w-32">
// //                               <SelectValue />
// //                             </SelectTrigger>
// //                             <SelectContent>
// //                               <SelectItem value="admin">Admin</SelectItem>
// //                               <SelectItem value="editor">Editor</SelectItem>
// //                               <SelectItem value="viewer">Viewer</SelectItem>
// //                             </SelectContent>
// //                           </Select>
// //                         </TableCell>
// //                         <TableCell>
// //                           <Badge className={getStatusColor(member.status)}>
// //                             <div className="flex items-center gap-1">
// //                               {getStatusIcon(member.status)}
// //                               {member.status}
// //                             </div>
// //                           </Badge>
// //                         </TableCell>
// //                         <TableCell>
// //                           {new Date(member.created_at).toLocaleDateString()}
// //                         </TableCell>
// //                         <TableCell>
// //                           <div className="flex gap-2">
// //                             {member.status === 'pending' && (
// //                               <Button
// //                                 variant="outline"
// //                                 size="sm"
// //                                 onClick={() => {
// //                                   const link = `${window.location.origin}/invite/${Math.random().toString(36).substring(2, 15)}`;
// //                                   navigator.clipboard.writeText(link);
// //                                   toast({
// //                                     title: "Link Copied",
// //                                     description: "Invitation link copied to clipboard"
// //                                   });
// //                                 }}
// //                               >
// //                                 <Copy className="h-4 w-4" />
// //                               </Button>
// //                             )}
// //                             {member.user_id !== user?.id && (
// //                               <Button
// //                                 variant="outline"
// //                                 size="sm"
// //                                 onClick={() => removeTeamMember(member.id, member.email)}
// //                                 className="text-red-600 hover:text-red-700"
// //                               >
// //                                 Remove
// //                               </Button>
// //                             )}
// //                           </div>
// //                         </TableCell>
// //                       </TableRow>
// //                     ))}
// //                   </TableBody>
// //                 </Table>
// //               )}
// //             </CardContent>
// //           </Card>
// //         </div>

// //         <div className="flex justify-end">
// //           <Button onClick={onClose}>Close</Button>
// //         </div>
// //       </DialogContent>
// //     </Dialog>
// //   );
// // };
